import { useEffect, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { CalendarIcon, PlaneTakeoff, Pencil, Trash2, Paperclip, Download } from "lucide-react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { TESTIDS } from "@/constants/testIds";
import { LEAVE_TYPES, STATUS_LABELS } from "@/constants/roles";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";

const statusPillCls = {
  pending: "bg-signal-absent text-white",
  approved: "bg-signal-ok text-white",
  rejected: "bg-signal-late text-white",
};

const ALLOWED_ATTACH = /\.(jpg|jpeg|png|pdf)$/i;

function fileToDataUrl(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result || ""));
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

export default function LeavePage() {
  const [items, setItems] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ leave_type: "cuti", start: null, end: null, reason: "", attachment_file: null });

  const load = async () => {
    setLoading(true);
    try {
      const [l, b] = await Promise.all([api.get("/leave/mine"), api.get("/leave/balance")]);
      setItems(l.data); setBalance(b.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ leave_type: "cuti", start: null, end: null, reason: "", attachment_file: null });
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      leave_type: r.leave_type,
      start: new Date(r.start_date + "T00:00:00"),
      end: new Date(r.end_date + "T00:00:00"),
      reason: r.reason || "",
      attachment_file: null,
    });
    setOpen(true);
  };

  const doDelete = async (r) => {
    if (!window.confirm(`Hapus pengajuan ${LEAVE_TYPES[r.leave_type]} ${r.start_date} → ${r.end_date}?`)) return;
    try {
      await api.delete(`/leave/${r.request_id}`);
      toast.success("Pengajuan dihapus.");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Gagal hapus.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.start || !form.end) return toast.error("Pilih tanggal mulai dan selesai.");
    if (form.end < form.start) return toast.error("Tanggal selesai harus setelah mulai.");
    const payload = {
      leave_type: form.leave_type,
      start_date: format(form.start, "yyyy-MM-dd"),
      end_date: format(form.end, "yyyy-MM-dd"),
      reason: form.reason,
    };
    if (form.attachment_file) {
      if (!ALLOWED_ATTACH.test(form.attachment_file.name)) {
        return toast.error("Attachment harus JPG, JPEG, PNG, atau PDF.");
      }
      try {
        payload.attachment_base64 = await fileToDataUrl(form.attachment_file);
        payload.attachment_filename = form.attachment_file.name;
      } catch (_e) {
        return toast.error("Gagal membaca file attachment.");
      }
    }
    try {
      if (editing) {
        await api.patch(`/leave/${editing.request_id}`, payload);
        toast.success("Pengajuan diperbarui.");
      } else {
        await api.post("/leave", payload);
        toast.success("Pengajuan dikirim.");
      }
      setOpen(false); setEditing(null);
      setForm({ leave_type: "cuti", start: null, end: null, reason: "", attachment_file: null });
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Gagal.");
    }
  };

  const attachmentUrl = (id) => {
    const t = localStorage.getItem("access_token");
    return `${API_BASE}/leave/${id}/attachment${t ? `?auth=${encodeURIComponent(t)}` : ""}`;
  };

  return (
    <div data-testid={TESTIDS.leavePage} className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-tiny">Cuti / Izin / Sakit / Dinas Luar</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">Cuti & Izin</h1>
        </div>
        <button data-testid={TESTIDS.leaveNewBtn} onClick={openCreate}
          className="inline-flex items-center gap-2 bg-klein text-white px-5 py-3 hover:bg-black transition-colors text-sm font-semibold">
          <PlaneTakeoff className="w-4 h-4" /> Ajukan
        </button>
      </div>

      {balance && (
        <div className="grid grid-cols-3 gap-4">
          <div data-testid="balance-quota" className="border border-black/10 bg-white p-5">
            <div className="label-tiny">Kuota Cuti {balance.year}</div>
            <div className="font-display text-4xl font-black tracking-tighter mt-2">{balance.quota}<span className="text-lg font-normal text-gray-500 ml-1">hari</span></div>
          </div>
          <div data-testid="balance-used" className="border border-black/10 bg-white p-5">
            <div className="label-tiny">Terpakai / Diproses</div>
            <div className="font-display text-4xl font-black tracking-tighter mt-2 text-signal-late">{balance.used}<span className="text-lg font-normal text-gray-500 ml-1">hari</span></div>
          </div>
          <div data-testid="balance-remaining" className="border-2 border-klein bg-klein/5 p-5">
            <div className="label-tiny text-klein">Sisa Saldo Cuti</div>
            <div className="font-display text-4xl font-black tracking-tighter mt-2 text-klein">{balance.remaining}<span className="text-lg font-normal text-klein/60 ml-1">hari</span></div>
          </div>
        </div>
      )}
      {balance && balance.quota > 0 && (
        <p className="text-xs text-gray-500 -mt-2">
          Catatan: hanya jenis <strong>Cuti</strong> yang mengurangi saldo. <strong>Sakit</strong> dan <strong>Izin</strong> tidak.
        </p>
      )}

      <div className="border border-black/10 bg-white overflow-x-auto">
        <table data-testid={TESTIDS.leaveTable} className="w-full text-sm min-w-[1000px]">
          <thead className="bg-black text-white text-left">
            <tr><Th>Jenis</Th><Th>Mulai</Th><Th>Selesai</Th><Th>Hari</Th><Th>Alasan</Th><Th>Attachment</Th><Th>Status</Th><Th>Progress Approval</Th><Th>Aksi</Th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="p-8 text-center text-gray-500">Memuat...</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-gray-500">Belum ada pengajuan.</td></tr>}
            {items.map((r) => (
              <tr key={r.request_id} className="border-t border-black/10 hover:bg-gray-50 align-top">
                <Td className="font-semibold">{LEAVE_TYPES[r.leave_type] || r.leave_type}</Td>
                <Td className="font-mono">{r.start_date}</Td>
                <Td className="font-mono">{r.end_date}</Td>
                <Td>{r.days}</Td>
                <Td className="max-w-xs text-gray-600">{r.reason}</Td>
                <Td>
                  {r.attachment_path ? (
                    <a data-testid={`leave-attach-${r.request_id}`} href={attachmentUrl(r.request_id)} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-klein hover:underline text-xs">
                      <Download className="w-3 h-3" /> {r.attachment_filename || "Lihat"}
                    </a>
                  ) : (
                    r.leave_type === "sakit" ? <span className="text-xs text-gray-400 italic">tanpa surat → Izin</span> : <span className="text-xs text-gray-400">—</span>
                  )}
                </Td>
                <Td><span className={`px-2 py-1 text-xs font-bold uppercase tracking-widest ${statusPillCls[r.status]}`}>{STATUS_LABELS[r.status]}</span></Td>
                <Td>
                  <ApprovalChain approvers={r.approvers} currentStep={r.current_step} />
                </Td>
                <Td>
                  {r.status === "pending" ? (
                    <div className="flex items-center gap-1">
                      <button data-testid={`leave-edit-${r.request_id}`} onClick={() => openEdit(r)} className="border border-black/20 hover:bg-black hover:text-white p-1.5" title="Edit">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button data-testid={`leave-delete-${r.request_id}`} onClick={() => doDelete(r)} className="border border-black/20 hover:bg-signal-late hover:text-white p-1.5" title="Hapus">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : <span className="text-xs text-gray-400">—</span>}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
        <DialogContent className="rounded-none max-w-lg border-black/20">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit Pengajuan" : "Ajukan Cuti / Izin / Sakit / Dinas Luar"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <Field label="Jenis">
              <select value={form.leave_type} onChange={(e) => setForm({ ...form, leave_type: e.target.value })} className="input">
                {Object.entries(LEAVE_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Tanggal Mulai">
                <DatePick value={form.start} onChange={(d) => setForm({ ...form, start: d })} />
              </Field>
              <Field label="Tanggal Selesai">
                <DatePick value={form.end} onChange={(d) => setForm({ ...form, end: d })} />
              </Field>
            </div>
            <Field label="Alasan">
              <textarea rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} required className="input" />
            </Field>

            <Field label={form.leave_type === "sakit" ? "Attachment (Surat Dokter — wajib agar diakui sebagai Sakit)" : "Attachment (opsional)"}>
              <label htmlFor="leave-attach-file" className="inline-flex items-center gap-2 border border-black/20 px-3 py-2 hover:bg-black hover:text-white cursor-pointer text-sm w-full">
                <Paperclip className="w-4 h-4" />
                {form.attachment_file ? form.attachment_file.name : "Pilih file (JPG, JPEG, PNG, PDF)"}
                <input id="leave-attach-file" data-testid="leave-attach-input" type="file" accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf" className="hidden"
                  onChange={(e) => setForm({ ...form, attachment_file: e.target.files?.[0] || null })} />
              </label>
              {form.leave_type === "sakit" && !form.attachment_file && !editing?.attachment_path && (
                <div className="text-xs text-gray-500 mt-1">Tanpa attachment, pengajuan Sakit akan otomatis dihitung sebagai <strong>Izin</strong>.</div>
              )}
              {editing?.attachment_path && !form.attachment_file && (
                <div className="text-xs text-gray-500 mt-1">Attachment sebelumnya: {editing.attachment_filename || "terlampir"} (kosongkan untuk mempertahankan)</div>
              )}
            </Field>
            <DialogFooter>
              <button type="button" onClick={() => { setOpen(false); setEditing(null); }} className="border border-black/20 px-4 py-2 hover:bg-gray-100 text-sm">Batal</button>
              <button data-testid={TESTIDS.leaveSubmit} type="submit" className="bg-klein text-white px-5 py-2 hover:bg-black text-sm font-semibold">{editing ? "Simpan" : "Ajukan"}</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <style>{`.input { width:100%; border: 1px solid rgba(0,0,0,0.2); padding: 10px 12px; background: white; font-size: 14px; }`}</style>
    </div>
  );
}

export function ApprovalChain({ approvers, currentStep }) {
  if (!approvers || approvers.length === 0) return <span className="text-xs text-gray-400">Tidak ada approver</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {approvers.map((a, i) => {
        let cls = "border-black/20 text-gray-500";
        if (a.status === "approved") cls = "bg-signal-ok text-white border-signal-ok";
        else if (a.status === "rejected") cls = "bg-signal-late text-white border-signal-late";
        else if (i === currentStep) cls = "bg-signal-absent text-white border-signal-absent";
        return (
          <span key={i} className={`px-2 py-1 text-[10px] border font-bold uppercase tracking-wider ${cls}`}
            title={`${a.name} — ${a.status}${a.note ? `: ${a.note}` : ""}`}>
            {i + 1}. {a.name}
          </span>
        );
      })}
    </div>
  );
}

function Field({ label, children }) { return <div><div className="label-tiny mb-2">{label}</div>{children}</div>; }
function DatePick({ value, onChange }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-start rounded-none border-black/20 bg-white hover:bg-gray-50">
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? format(value, "dd MMM yyyy") : "Pilih tanggal"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 rounded-none" align="start">
        <Calendar mode="single" selected={value} onSelect={onChange} initialFocus />
      </PopoverContent>
    </Popover>
  );
}
const Th = ({ children }) => <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
